// src/components/product/ProductsSearchPanel.tsx
//
// ProductsSearchPanel
// - Presentation-only search UI for the Products page.
// - Delegates all state ownership to the parent page.
// - Uses the shared SearchPanel container for:
//   - Search / Reset buttons (single source of truth)
//   - Optional auto-search (debounced) controlled by the page via `autoSearch` prop
//   - Active filter chips (removable, with readable labels)
//
// IMPORTANT (policy):
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel has global defaults (enabled: false, delayMs: 500) via ./constants.
//   This component should only pass through `autoSearch` if provided by the page.

import React, { useMemo } from 'react';
import { Autocomplete, Grid, TextField } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import SearchPanel from '@components/common/search/SearchPanel';
import type { SearchPanelAction, SearchChipSpec } from '@components/common/search/types';

import { ProductSaleType, type GetProductsParams } from '@apis/product';
import { ProductStatus } from '@models/product';

import { PRODUCT_SALE_TYPE_LABELS, PRODUCT_STATUS_LABELS } from '@utils/productLabels';

type ProductSearchForm = GetProductsParams;

type Option<T> = {
  label: string;
  value: T;
};

export type ProductsSearchPanelProps = {
  searchForm: ProductSearchForm;

  /**
   * Shared input contract (generic + null to clear).
   * The parent page owns draft state and decides how to normalize/apply it.
   */
  onChangeInput: <K extends keyof ProductSearchForm>(name: K, value: ProductSearchForm[K] | null) => void;

  onSearch: () => void;
  onReset: () => void;

  onExport: () => void;
  isExporting?: boolean;

  /**
   * Auto-search configuration provided by the page.
   * If omitted, SearchPanel falls back to its global defaults.
   */
  autoSearch?: { enabled: boolean; delayMs?: number };

  countryOptions: Array<Option<number>>;
  stockOptions: Array<Option<number>>;
};

const ProductsSearchPanel: React.FC<ProductsSearchPanelProps> = ({
  searchForm,
  onChangeInput,
  onSearch,
  onReset,
  onExport,
  isExporting = false,
  autoSearch,
  countryOptions,
  stockOptions,
}) => {
  /* ------------------------------------------------------------------ */
  /* Options                                                            */
  /* ------------------------------------------------------------------ */

  const statusOptions: Array<Option<ProductStatus>> = useMemo(
    () =>
      (Object.values(ProductStatus) as ProductStatus[]).map((s) => ({
        value: s,
        label: PRODUCT_STATUS_LABELS[s] ?? String(s),
      })),
    [],
  );

  const saleTypeOptions: Array<Option<ProductSaleType>> = useMemo(
    () =>
      (Object.values(ProductSaleType) as ProductSaleType[]).map((t) => ({
        value: t,
        label: PRODUCT_SALE_TYPE_LABELS[t] ?? String(t),
      })),
    [],
  );

  /* ------------------------------------------------------------------ */
  /* Lookup maps (O(1) label access for chips + safety fallbacks)        */
  /* ------------------------------------------------------------------ */

  const countryLabelById = useMemo(() => {
    const map = new Map<number, string>();
    countryOptions.forEach((o) => map.set(Number(o.value), o.label));
    return map;
  }, [countryOptions]);

  const stockLabelById = useMemo(() => {
    const map = new Map<number, string>();
    stockOptions.forEach((o) => map.set(Number(o.value), o.label));
    return map;
  }, [stockOptions]);

  const saleTypeLabelByValue = useMemo(() => {
    const map = new Map<ProductSaleType, string>();
    saleTypeOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [saleTypeOptions]);

  const statusLabelByValue = useMemo(() => {
    const map = new Map<ProductStatus, string>();
    statusOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [statusOptions]);

  /* ------------------------------------------------------------------ */
  /* Selected values (for Autocomplete)                                  */
  /* ------------------------------------------------------------------ */

  const selectedCountry = useMemo(() => {
    const id = searchForm.countryId;
    if (typeof id !== 'number') return null;
    return countryOptions.find((o) => Number(o.value) === Number(id)) ?? null;
  }, [countryOptions, searchForm.countryId]);

  const selectedStock = useMemo(() => {
    const id = searchForm.stockId;
    if (typeof id !== 'number') return null;
    return stockOptions.find((o) => Number(o.value) === Number(id)) ?? null;
  }, [stockOptions, searchForm.stockId]);

  const selectedStatus = useMemo(() => {
    const v = searchForm.productStatus as ProductStatus | undefined;
    if (!v) return null;
    return statusOptions.find((o) => o.value === v) ?? null;
  }, [searchForm.productStatus, statusOptions]);

  const selectedSaleType = useMemo(() => {
    const v = searchForm.productSaleType as ProductSaleType | undefined;
    if (!v) return null;
    return saleTypeOptions.find((o) => o.value === v) ?? null;
  }, [saleTypeOptions, searchForm.productSaleType]);

  /* ------------------------------------------------------------------ */
  /* Actions                                                            */
  /* ------------------------------------------------------------------ */

  const actions: SearchPanelAction[] = useMemo(
    () => [
      {
        key: 'export',
        label: 'Export',
        startIcon: <FileDownloadIcon />,
        variant: 'outlined',
        onClick: onExport,
        disabled: isExporting,
      },
    ],
    [isExporting, onExport],
  );

  /* ------------------------------------------------------------------ */
  /* Chips                                                              */
  /* ------------------------------------------------------------------ */

  const chipSpecs: Array<SearchChipSpec<ProductSearchForm>> = useMemo(
    () => [
      {
        key: 'productCode',
        label: 'Product Code',
        isActive: (s) => Boolean(String(s.productCode ?? '').trim()),
        getValueText: (s) => String(s.productCode ?? '').trim(),
        clear: ({ setValue }) => setValue('productCode', '' as any),
      },
      {
        key: 'productName',
        label: 'Product Name',
        isActive: (s) => Boolean(String(s.productName ?? '').trim()),
        getValueText: (s) => String(s.productName ?? '').trim(),
        clear: ({ setValue }) => setValue('productName', '' as any),
      },
      {
        key: 'countryId',
        label: 'Country',
        isActive: (s) => typeof s.countryId === 'number',
        getValueText: (s) => {
          const id = s.countryId;
          if (typeof id !== 'number') return '';
          return countryLabelById.get(id) ?? String(id);
        },
        clear: ({ setValue }) => setValue('countryId', null as any),
      },
      {
        key: 'stockId',
        label: 'Stock Code',
        isActive: (s) => typeof s.stockId === 'number',
        getValueText: (s) => {
          const id = s.stockId;
          if (typeof id !== 'number') return '';
          return stockLabelById.get(id) ?? String(id);
        },
        clear: ({ setValue }) => setValue('stockId', null as any),
      },
      {
        key: 'productSaleType',
        label: 'Order Type',
        isActive: (s) => Boolean(s.productSaleType),
        getValueText: (s) => {
          const v = s.productSaleType as ProductSaleType | undefined;
          if (!v) return '';
          return saleTypeLabelByValue.get(v) ?? PRODUCT_SALE_TYPE_LABELS[v] ?? String(v);
        },
        clear: ({ setValue }) => setValue('productSaleType', null as any),
      },
      {
        key: 'productStatus',
        label: 'Status',
        isActive: (s) => Boolean(s.productStatus),
        getValueText: (s) => {
          const v = s.productStatus as ProductStatus | undefined;
          if (!v) return '';
          return statusLabelByValue.get(v) ?? PRODUCT_STATUS_LABELS[v] ?? String(v);
        },
        clear: ({ setValue }) => setValue('productStatus', null as any),
      },
    ],
    [countryLabelById, saleTypeLabelByValue, statusLabelByValue, stockLabelById],
  );

  // ✅ 지침 강화: enabled=true인 경우에만 패스스루
  const panelAutoSearch = autoSearch?.enabled === true ? autoSearch : undefined;

  return (
    <SearchPanel<ProductSearchForm>
      title="Filters"
      onSearch={onSearch}
      onReset={onReset}
      actions={actions}
      // Pass-through only. If undefined, SearchPanel uses global defaults.
      autoSearch={panelAutoSearch}
      chipsConfig={{
        enabled: true,
        search: searchForm,
        setValue: (key, value) => onChangeInput(key as any, value as any),
        chips: chipSpecs,
        maxVisible: 2,
        // Keep behavior consistent with other pages:
        // chip removal should immediately run a search.
        onAfterClear: onSearch,
      }}
    >
      <Grid container spacing={2}>
        {/* Row 1 */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Product Code"
            placeholder="Enter product code"
            size="small"
            fullWidth
            value={searchForm.productCode ?? ''}
            onChange={(e) => onChangeInput('productCode', e.target.value as any)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Product Name"
            placeholder="Enter product name"
            size="small"
            fullWidth
            value={searchForm.productName ?? ''}
            onChange={(e) => onChangeInput('productName', e.target.value as any)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<number>, false, false, false>
            size="small"
            options={countryOptions}
            value={selectedCountry}
            onChange={(_, option) => onChangeInput('countryId', (option?.value ?? null) as any)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => Number(a.value) === Number(b.value)}
            renderInput={(params) => <TextField {...params} label="Country" placeholder="Select country" />}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<number>, false, false, false>
            size="small"
            options={stockOptions}
            value={selectedStock}
            onChange={(_, option) => onChangeInput('stockId', (option?.value ?? null) as any)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => Number(a.value) === Number(b.value)}
            renderInput={(params) => <TextField {...params} label="Stock Code" placeholder="Select stock code" />}
          />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<ProductSaleType>, false, false, false>
            size="small"
            options={saleTypeOptions}
            value={selectedSaleType}
            onChange={(_, option) => onChangeInput('productSaleType', (option?.value ?? null) as any)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => <TextField {...params} label="Order Type" placeholder="Select order type" />}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<ProductStatus>, false, false, false>
            size="small"
            options={statusOptions}
            value={selectedStatus}
            onChange={(_, option) => onChangeInput('productStatus', (option?.value ?? null) as any)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => <TextField {...params} label="Status" placeholder="Select status" />}
          />
        </Grid>
      </Grid>
    </SearchPanel>
  );
};

export default ProductsSearchPanel;
